import polygonClipping from "polygon-clipping";
import { area, bbox, segIntersect, toLocal, worldPoints } from "./geometry";
import { thin } from "./pairs";

// Manual eyebrow: keep the top `depth` inches of a windshield (local coords are y-down, so the glass top is the smallest y).
export const EYEBROW_DEFAULT = { depth: 6, mode: "straight" };
export const EYEBROW_MIN = 1;
export const clampDepth = (src, d) => {
  const bb = bbox(src), max = Math.max(EYEBROW_MIN, Math.round((bb.h - 1) * 100) / 100);
  return Math.min(Math.max(EYEBROW_MIN, Math.round(d * 100) / 100), max);
};

const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

// Upper boundary sampled per column → [x, yTop]
function topSamples(pts, bb, step = 0.5) {
  const bins = Math.max(8, Math.ceil(bb.w / step)), w = bb.w / bins, out = new Array(bins).fill(null);
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 0.1));
    for (let k = 0; k < n; k++) {
      const p = lerp(a, b, k / n), j = Math.min(bins - 1, Math.max(0, Math.floor((p[0] - bb.minX) / w)));
      if (out[j] === null || p[1] < out[j]) out[j] = p[1];
    }
  }
  return out.map((y, j) => (y === null ? null : [bb.minX + (j + 0.5) * w, y])).filter(Boolean);
}

// The windshield's real top edge as a function of x: per-column contour with the mirror notch and the steep pillar corners
// rejected (robust parabola residual), lightly smoothed and linearly interpolated; flat beyond the kept range.
export function topCurve(pts) {
  const bb = bbox(pts);
  const all = topSamples(pts, bb);
  // a column is rejected when it sits > 0.3" lower than the median of its ±8" neighbourhood (notch) or > 0.5" lower
  // than the highest column within ±3" (pillar corner ramp)
  const samples = all.filter(([x, y]) => {
    const near = all.filter(([x2]) => Math.abs(x2 - x) <= 8).map((s) => s[1]).sort((a, b) => a - b);
    const hi = Math.min(...all.filter(([x2]) => Math.abs(x2 - x) <= 3).map((s) => s[1]));
    return y - near[Math.floor(near.length / 2)] < 0.3 && y - hi < 0.5;
  });
  if (samples.length < 3) return () => bb.minY;
  const sm = samples.map(([x], i) => [x, (samples[Math.max(0, i - 1)][1] + samples[i][1] + samples[Math.min(samples.length - 1, i + 1)][1]) / 3]);
  return (x) => {
    if (x <= sm[0][0]) return sm[0][1];
    if (x >= sm[sm.length - 1][0]) return sm[sm.length - 1][1];
    let i = 1; while (sm[i][0] < x) i++;
    const [x0, y0] = sm[i - 1], [x1, y1] = sm[i];
    return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0 || 1);
  };
}

// Cut the outline along y = f(x): straight = horizontal line `depth` below the top edge at the centre of the glass,
// curve = the top edge itself offset down by `depth` (constant height everywhere).
// Returns the kept (upper) polygon, the cut line for display, an inside() test and the result size — or null.
export function eyebrowCut(src, depth, mode = "straight") {
  if (!src || src.length < 3) return null;
  const bb = bbox(src);
  const top = topCurve(src);
  const f = mode === "curve" ? (x) => top(x) + depth : (() => { const y = top(bb.cx) + depth; return () => y; })();
  const inside = ([x, y]) => y <= f(x) + 1e-9;
  const cross = (a, b) => {
    let lo = 0, hi = 1; const ia = inside(a);
    for (let k = 0; k < 40; k++) { const m = (lo + hi) / 2; if (inside(lerp(a, b, m)) === ia) lo = m; else hi = m; }
    return lerp(a, b, (lo + hi) / 2);
  };
  const out = [];
  for (let i = 0; i < src.length; i++) {
    const a = src[i], b = src[(i + 1) % src.length], ia = inside(a), ib = inside(b);
    if (ia) { out.push({ p: a }); if (!ib) out.push({ p: cross(a, b), exit: true }); }
    else if (ib) out.push({ p: cross(a, b), entry: true });
  }
  if (out.length < 3) return null;
  const points = [];
  for (let i = 0; i < out.length; i++) {
    const cur = out[i], nxt = out[(i + 1) % out.length];
    points.push(cur.p);
    if (mode === "curve" && cur.exit && nxt.entry) {
      const x0 = cur.p[0], x1 = nxt.p[0], n = Math.ceil(Math.abs(x1 - x0) / 0.15);
      for (let k = 1; k < n; k++) { const x = x0 + (x1 - x0) * (k / n); points.push([x, f(x)]); }
    }
  }
  const x0 = bb.minX - 0.75, x1 = bb.maxX + 0.75, n = mode === "curve" ? Math.ceil((x1 - x0) / 0.25) : 1;
  const line = Array.from({ length: n + 1 }, (_, k) => { const x = x0 + (x1 - x0) * (k / n); return [x, f(x)]; });
  return { points, line, inside, size: bbox(points) };
}

const JOIN_EPS = 0.02;   // the neighbour is pushed this far into the kept part so the union never leaves a hairline seam
const JOIN_MAX_GAP = 3;  // parts further apart than this were not placed as one windshield — no guessing
const closestOnSeg = ([px, py], [ax, ay], [bx, by]) => { const vx = bx - ax, vy = by - ay, l2 = vx * vx + vy * vy || 1e-12; const t = Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / l2)); return [ax + vx * t, ay + vy * t]; };

// Closest boundary points of two outlines → { d, a: [x, y] on A, b: [x, y] on B } (d = 0 when they cross).
function closestPair(A, B) {
  let best = { d: Infinity, a: null, b: null };
  const consider = (pa, pb) => { const d = Math.hypot(pa[0] - pb[0], pa[1] - pb[1]); if (d < best.d) best = { d, a: pa, b: pb }; };
  for (let i = 0; i < A.length; i++) {
    const a1 = A[i], a2 = A[(i + 1) % A.length];
    for (let j = 0; j < B.length; j++) {
      const b1 = B[j], b2 = B[(j + 1) % B.length];
      if (segIntersect(a1, a2, b1, b2)) return { d: 0, a: a1, b: b1 };
      consider(a1, closestOnSeg(a1, b1, b2)); consider(a2, closestOnSeg(a2, b1, b2));
      consider(closestOnSeg(b1, a1, a2), b1); consider(closestOnSeg(b2, a1, a2), b2);
    }
  }
  return best;
}

// A split windshield (2+ parts of one set) as it sits on the roll → ONE outline in `ref`'s local frame, the glass as a single
// piece: each neighbour slides straight across the seam until it touches (gap closed, no cut line left), then the outlines are
// unioned. null when the parts don't line up (too far apart, stacked on each other, or the union is not one shape).
export function joinParts(ref, parts) {
  const rest = parts.filter((p) => p.id !== ref.id).map((p) => worldPoints(p));
  if (!rest.length) return null;
  const total = parts.reduce((s, p) => s + area(p.points), 0);
  let cur = worldPoints(ref);
  while (rest.length) {
    let bi = 0, best = null;
    rest.forEach((w, i) => { const c = closestPair(cur, w); if (!best || c.d < best.d) { best = c; bi = i; } });
    const w = rest.splice(bi, 1)[0];
    if (best.d > JOIN_MAX_GAP) return null;
    let moved = w;
    if (best.d > 1e-6) {
      const ux = (best.a[0] - best.b[0]) / best.d, uy = (best.a[1] - best.b[1]) / best.d, s = best.d + JOIN_EPS;
      moved = w.map(([x, y]) => [x + ux * s, y + uy * s]);
    }
    const u = polygonClipping.union([cur], [moved]);
    if (u.length !== 1) return null;
    cur = u[0][0].slice(0, -1);
  }
  if (area(cur) < 0.9 * total) return null;  // parts stacked on top of each other, not side by side
  return thin(cur.map((p) => toLocal(ref, p)), 0.01);
}

// What an eyebrow is cut from: a split windshield with all its parts on the roll is joined into one outline first
// (one visor, no seam); otherwise the piece's own outline. `parts` lists the joined pieces (empty when nothing was joined).
export function eyebrowSource(piece, pieces) {
  if (!piece) return null;
  if (piece.eyebrowSource) return { src: piece.eyebrowSource, parts: [], joinFailed: false };
  const parts = piece.setKey && piece.pieceType === "windshield" ? pieces.filter((p) => p.setKey === piece.setKey && p.pieceType === "windshield" && p.included) : [];
  if (parts.length > 1) {
    const joined = joinParts(piece, parts);
    if (joined) return { src: joined, parts, joinFailed: false };
    return { src: piece.points, parts: [], joinFailed: true };
  }
  return { src: piece.points, parts: [], joinFailed: false };
}

// Split along the vertical line x = cx; keepLeft → the part with x <= cx (an eyebrow becomes a left + right half for narrow film)
export function clipX(pts, cx, keepLeft) {
  const inside = ([x]) => (keepLeft ? x <= cx + 1e-9 : x >= cx - 1e-9);
  const cross = (a, b) => { const t = (cx - a[0]) / (b[0] - a[0] || 1e-9); return [cx, a[1] + (b[1] - a[1]) * t]; };
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length], ia = inside(a), ib = inside(b);
    if (ia) { out.push(a); if (!ib) out.push(cross(a, b)); } else if (ib) out.push(cross(a, b));
  }
  return out;
}
